import React, { useEffect, useState, useContext } from 'react';

import {
    deliveryHeadCellStyle,
    deliveryImpactsContainerStyle,
    deliveryMainCellStyle,
    deliverySumCellStyle,
    deliveryTextStyle,
    deliveryTotalCellStyle,
    impactsTableStyle
} from '../AirtimePlanningStyles';
import { digitCommaFormat, addDays } from '../AirtimePlanningUtils';
import { SystemSettingsContext } from '../../../../contexts/SystemSettingsContext';

const DeliveryImpacts = ({ planSummary, hasPlan, showImpacts, showBaseImpacts, setTotalAirtimeCost, ...style }) => {
    const { formatCurrency } = useContext(SystemSettingsContext);
    const [tableData, setTableData] = useState({});
    const [rowTotals, setRowTotals] = useState({});
    const [colTotals, setColTotals] = useState({});
    const [totalImpacts, setTotalImpacts] = useState(0);
    const [rowIndexes, setRowIndexes] = useState([]);

    const calculateTableData = () => {
        const newData = {};
        const newRowTotals = {};
        const newColTotals = {};
        const rIndexes = [];
        let newTotal = 0;
        for (let i = 0; i < planSummary?.length; i++) {
            if (!rIndexes.includes(planSummary[i]?.dayPartName)) rIndexes.push(planSummary[i]?.dayPartName);
            const iDay = addDays(planSummary[i]?.orderedDate, 0)?.getDay();
            if (iDay >= 0) {
                const value = showBaseImpacts
                    ? planSummary[i]?.baseImpacts
                    : showImpacts
                        ? planSummary[i]?.targetImpacts
                        : planSummary[i]?.price;

                if (!newData[planSummary[i]?.dayPartName]) newData[planSummary[i]?.dayPartName] = {};
                if (!newData[planSummary[i]?.dayPartName][iDay]) newData[planSummary[i]?.dayPartName][iDay] = 0;
                newData[planSummary[i]?.dayPartName][iDay] += value;

                if (!newRowTotals[planSummary[i]?.dayPartName]) newRowTotals[planSummary[i]?.dayPartName] = 0;
                newRowTotals[planSummary[i]?.dayPartName] += value;

                if (!newColTotals[iDay]) newColTotals[iDay] = 0;
                newColTotals[iDay] += value;

                newTotal += value;
            }
        }

        setTableData(newData);
        setRowTotals(newRowTotals);
        setColTotals(newColTotals);
        setRowIndexes(rIndexes.sort());
        setTotalImpacts(newTotal);
    };

    const blankTableData = () => {
        const newData = {};
        for (let i = 0; i < 6; i++) {
            newData[i] = '';
        }
        setTableData(newData);
    };

    const calculateTotalAirttimeCost = () => {
        let newTotal = 0;
        for (let i = 0; i < planSummary?.length; i++) {
            const iDay = addDays(planSummary[i]?.orderedDate, 0)?.getDay();
            if (iDay >= 0) {
                newTotal += planSummary[i]?.price;
            }
        }
        setTotalAirtimeCost(newTotal);
    };

    useEffect(() => {
        if (planSummary && hasPlan) {
            calculateTotalAirttimeCost();
        }
    }, [planSummary, hasPlan]);

    useEffect(() => {
        if (planSummary && hasPlan) {
            calculateTableData();
        } else {
            blankTableData();
        }
    }, [planSummary, hasPlan, showImpacts, showBaseImpacts]);

    const tableDataByCell = (rowIndex, colIndex) => {
        return !hasPlan
            ? ''
            : showBaseImpacts || showImpacts
                ? digitCommaFormat(tableData[rowIndexes[rowIndex]]?.[colIndex]) || '0'
                : formatCurrency(tableData[rowIndexes[rowIndex]]?.[colIndex]);
    };

    const formatTableData = (value) => {
        return !hasPlan
            ? ''
            : showBaseImpacts || showImpacts
                ? digitCommaFormat(value ?? 0) || '0'
                : formatCurrency(value);
    };

    const displayImpactTotal = (value) => {
        return !hasPlan
            ? ''
            : (value ?? 0) || '0'
    };

    const tableRowStyle = { pageBreakInside: 'avoid', pageBreakAfter: 'auto' };

    return (
        <div style={deliveryImpactsContainerStyle}>
            <table style={impactsTableStyle}>
                <thead>
                    <tr style={tableRowStyle}>
                        <th>
                            {window.translate(
                                showBaseImpacts
                                    ? 'Total Base Impacts'
                                    : showImpacts
                                        ? 'Total Target Impacts'
                                        : 'Total Spend'
                            )}
                        </th>

                        <th colSpan='9' style={deliveryHeadCellStyle}>
                            {window.translate(
                                showBaseImpacts ? 'Base Impacts' : showImpacts ? 'Target Impacts' : 'Costs'
                            )}
                        </th>
                    </tr>

                    <tr style={tableRowStyle}>
                        <th style={{...deliveryHeadCellStyle, width:'10.28%'}}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Daypart')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Mon')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Tue')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Wed')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Thu')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Fri')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Sat')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : window.translate('Sun')}</span>
                        </th>

                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>
                                {!hasPlan
                                    ? ''
                                    : window.translate(
                                        showBaseImpacts
                                            ? 'Total Base Impacts'
                                            : showImpacts
                                                ? 'Impacts Total'
                                                : 'Costs Total'
                                    )}
                            </span>
                        </th>
                        <th style={deliveryHeadCellStyle}>
                            <span style={deliveryTextStyle}>
                                {!hasPlan
                                    ? ''
                                    : window.translate(
                                        showBaseImpacts
                                            ? 'Base Impacts Total %'
                                            : showImpacts
                                                ? 'Target Impacts Total %'
                                                : 'Costs Total %'
                                    )}
                            </span>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {Object.keys(tableData)?.map((dp, i) => (
                        <tr key={`daypart${i}-spots`} style={tableRowStyle}>
                            <td
                                style={{
                                    ...deliveryMainCellStyle(i),
                                    paddingLeft: 19.5,
                                    width: showBaseImpacts || showImpacts ? 197 : 223,
                                    height: showBaseImpacts || showImpacts ? 35 :40
                                }}
                            >
                                <span style={deliveryTextStyle}>{!hasPlan ? '' : rowIndexes[i] ?? ''}</span>
                            </td>
                            <td align='right' style={deliveryMainCellStyle(i)}>
                                <span style={deliveryTextStyle}>{tableDataByCell(i, 1)}</span>
                            </td>
                            <td align='right' style={deliveryMainCellStyle(i)}>
                                <span style={deliveryTextStyle}>{tableDataByCell(i, 2)}</span>
                            </td>
                            <td align='right' style={deliveryMainCellStyle(i)}>
                                <span style={deliveryTextStyle}>{tableDataByCell(i, 3)}</span>
                            </td>
                            <td align='right' style={deliveryMainCellStyle(i)}>
                                <span style={deliveryTextStyle}>{tableDataByCell(i, 4)}</span>
                            </td>
                            <td align='right' style={deliveryMainCellStyle(i)}>
                                <span style={deliveryTextStyle}>{tableDataByCell(i, 5)}</span>
                            </td>
                            <td align='right' style={deliveryMainCellStyle(i)}>
                                <span style={deliveryTextStyle}>{tableDataByCell(i, 6)}</span>
                            </td>
                            <td align='right' style={deliveryMainCellStyle(i)}>
                                <span style={deliveryTextStyle}>{tableDataByCell(i, 0)}</span>
                            </td>
                            <td align='right' style={deliveryTotalCellStyle(i)}>
                                <span style={deliveryTextStyle}>{formatTableData(rowTotals[rowIndexes[i]])}</span>
                            </td>
                            <td align='right' style={deliveryTotalCellStyle(i)}>
                                <span style={deliveryTextStyle}>{displayImpactTotal(Math.round(((rowTotals[rowIndexes[i]]) / totalImpacts) * 100))}</span>
                            </td>
                        </tr>
                    ))}
                    <tr style={tableRowStyle}>
                        <td
                            style={{
                                ...deliverySumCellStyle,
                                paddingLeft: 19.5,
                                width: 197
                            }}
                        >
                            <span style={deliveryTextStyle}>{!hasPlan ? '' : 'Total'}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(colTotals[1])}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(colTotals[2])}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(colTotals[3])}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(colTotals[4])}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(colTotals[5])}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(colTotals[6])}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(colTotals[0])}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{formatTableData(totalImpacts)}</span>
                        </td>
                        <td align='right' style={deliverySumCellStyle}>
                            <span style={deliveryTextStyle}>{displayImpactTotal(((totalImpacts / totalImpacts) * 100))}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default DeliveryImpacts;
